import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Loader2, TrendingUp } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine } from "recharts";

const fmtM = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${(n / 1000).toFixed(0)}k`;

export default function InvestmentProfile({ client }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!client) return;
    setData(null); setErr(null);
    api.get("/capital/investment-profile", { params: { client } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Profile unavailable"));
  }, [client]);

  if (err) return null;
  if (!data) {
    return <div className="flex items-center gap-2 text-xs text-slate-500 py-6 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Building investment profile…</div>;
  }

  const chart = data.years.map((y, i) => ({
    year: `${y.year}`, need: Math.round(y.need + (i === 0 ? data.backlog : 0)),
    smoothed: Math.round(data.smoothed[i].budget),
  }));
  const conf = data.confidence;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mt-5" data-testid="investment-profile">
      <div className="flex items-start gap-2 mb-1">
        <TrendingUp className="w-4 h-4 text-gbe mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-slate-800">10-Year Investment Profile</div>
          <div className="text-[11px] text-slate-500">
            Raw replacement need per year vs a smoothed budget (beyond-design-life backlog spread over the first 3 years).
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
        <Stat label="Backlog (beyond design life)" val={fmtCurrency(data.backlog)} sub={`${data.backlog_assets} assets`} tone="rose" testid="ip-backlog" />
        <Stat label="Total 10-year need" val={fmtCurrency(data.total_10yr)} testid="ip-total" />
        <Stat label="Flat annual budget" val={fmtCurrency(data.flat_annual)} sub="constant number for budgeting" tone="navy" testid="ip-flat" />
        <Stat label="Beyond 10 years" val={fmtCurrency(data.beyond_10yr)} testid="ip-beyond" />
      </div>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart} margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={fmtM} tick={{ fontSize: 11 }} width={54} />
            <Tooltip formatter={(v, n) => [fmtCurrency(v), n === "need" ? "Raw need" : "Smoothed budget"]} />
            <Legend formatter={(v) => v === "need" ? "Raw replacement need" : "Smoothed budget (backlog over 3 yrs)"}
                    wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="need" fill="#94a3b8" radius={[3, 3, 0, 0]} />
            <Line dataKey="smoothed" stroke="#003a5c" strokeWidth={2} dot={{ r: 3 }} type="monotone" />
            <ReferenceLine y={data.flat_annual} stroke="#0d9488" strokeDasharray="6 4"
                           label={{ value: `Flat ${fmtM(data.flat_annual)}/yr`, position: "insideTopRight", fontSize: 10, fill: "#0d9488" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2 text-[11px] text-amber-800" data-testid="ip-confidence-note">
        <span className="font-semibold">Data confidence:</span>{" "}
        {conf.modelled_default_life} of {conf.total_assets} assets ({conf.modelled_pct}%) sit on modelled default lives
        (exactly 5 or 10 years remaining), which concentrates spend into those years. Statuses are modelled from design
        life, not observed condition — site validation will smooth these peaks.
        {conf.no_remaining_life > 0 && ` ${conf.no_remaining_life} assets have no remaining-life data and are excluded.`}
      </div>
    </div>
  );
}

function Stat({ label, val, sub, tone, testid }) {
  return (
    <div data-testid={testid}
         className={`rounded-lg border p-3 ${tone === "rose" ? "border-rose-200 bg-rose-50/40" : tone === "navy" ? "border-[#003a5c]/30 bg-[#003a5c]/5" : "border-slate-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{val}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
